import { Popover } from '@mui/material'
import React, { useEffect, useState } from 'react'

export interface InfoBoxItem<T = HTMLElement> {
	content: React.ReactNode
	props?: Partial<React.ComponentProps<typeof Popover>>
	anchor: React.RefObject<T>
}

interface InfoBoxProps {
	infoItems: InfoBoxItem[]
	open: boolean
	onClose: () => void
}

const InfoBox: React.FC<InfoBoxProps> = ({ infoItems, open, onClose }) => {
	const [index, setIndex] = useState(0)
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

	useEffect(() => {
		if (open && infoItems[index]) {
			const anchor = infoItems[index].anchor.current

			if (anchor) {
				const rect = anchor.getBoundingClientRect()
				const isFullyVisible =
					rect.top >= 0 &&
					rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)

				if (!isFullyVisible) {
					anchor.scrollIntoView({ behavior: 'smooth' })
				}

				setAnchorEl(anchor)
			}
		} else {
			setAnchorEl(null)
		}
	}, [index, open, infoItems])

	const handleClose = () => {
		setAnchorEl(null)
		setTimeout(() => {
			if (index + 1 < infoItems.length) {
				setIndex(i => i + 1)
			} else {
				setIndex(0)
				onClose()
			}
		}, 200)
	}

	return (
		<Popover
			anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			open={!!anchorEl}
			anchorEl={anchorEl}
			onClose={handleClose}
			{...infoItems[index].props}
		>
			<div onClick={handleClose} className='p-2 max-w-xl'>
				{infoItems[index]?.content}
			</div>
		</Popover>
	)
}

export default InfoBox
